// HistoryTable.jsx (frontend copy)
import React, { useEffect, useMemo, useState } from 'react';
import JSONViewer from '../ui/JSONViewer';
// Render derived analysis results instead of raw JSON in the View dialog
function deriveSpecFromRecord(rec) {
  const base = rec || {}; const r = base.raw || {}; const parsed = r.parsed || r; const pick = (k) => base[k] ?? parsed[k] ?? null; const n = (v) => typeof v === 'string' ? v.trim() : (v == null ? null : String(v));
  return {
    material: n(pick('material')), lasttyp: n(pick('lasttyp')), forpackning: n(pick('forpackning')),
    stapelbarhet: n(pick('stapelbarhet')), trycktalighet: n(pick('trycktalighet')),
    viktkategori: n(pick('viktkategori')), omtalighet: n(pick('omtalighet')), form: n(pick('form')),
  };
}

const cn = (...c) => c.filter(Boolean).join(' ');
const Button = ({ className='', variant='outline', disabled, ...rest }) => (
  <button
    {...rest}
    disabled={disabled}
    className={cn(
      'inline-flex items-center justify-center gap-2 rounded-md border px-2.5 py-1.5 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      'border-default/50 bg-surface hover:bg-elevated/60 disabled:opacity-50 disabled:cursor-not-allowed',
      variant === 'primary' && 'bg-[color:var(--lodia-brand)] text-white hover:opacity-90 border-transparent',
      className
    )}
  />
);
const Card = ({ className='', ...rest }) => <div className={cn('rounded-2xl border bg-elevated/20 p-6 shadow-sm', className)} {...rest} />;
const Input = (props) => <input {...props} className={cn('w-full rounded-md border bg-surface px-3 py-2 text-sm outline-none focus:ring-2', props.className)} />;
const Select = ({ value, onChange, children, className }) => (
  <select value={value} onChange={onChange} className={cn('rounded-md border bg-surface px-2 py-2 text-sm', className)}>{children}</select>
);
const Badge = ({ children, className='' }) => <span className={cn('inline-block rounded-full bg-[color:var(--lodia-brand)]/15 px-2 py-0.5 text-[10px] font-medium text-[color:var(--lodia-brand)]', className)}>{children}</span>;
const Progress = ({ value }) => {
  const pct = Math.max(0, Math.min(100, value));
  const tone = pct >= 85 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="h-1.5 w-full rounded-full bg-black/10">
      <div className={cn('h-full rounded-full transition-all', tone)} style={{ width: `${pct}%` }} />
    </div>
  );
};
const Dialog = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" role="dialog" aria-modal="true">
      <div className="max-h-[90vh] w-full max-w-xl overflow-auto rounded-2xl border bg-surface p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold">{title}</h3>
          <Button onClick={onClose} aria-label="Close dialog">✕</Button>
        </div>
        {children}
      </div>
    </div>
  );
};
const Skeleton = ({ className='' }) => <div className={cn('animate-pulse rounded-md bg-black/10', className)} />;

export function HistoryTable({ fetchPage, refreshSignal, formatConfidence, onDelete, initialPageSize = 10 }) {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [labelFilter, setLabelFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [minConf, setMinConf] = useState('');
  const [maxConf, setMaxConf] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [viewImage, setViewImage] = useState(null);
  const [viewJSON, setViewJSON] = useState(null);

  React.useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 300); return () => clearTimeout(t); }, [q]);

  const load = React.useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { items, total } = await fetchPage({
        q: debouncedQ, label: labelFilter, from: fromDate, to: toDate,
        minConf: minConf || undefined, maxConf: maxConf || undefined,
        page, pageSize,
      });
      setRows(items);
      setTotal(total);
    } catch (e) { setError(e.message || 'Failed to load'); } finally { setLoading(false); }
  }, [debouncedQ, labelFilter, fromDate, toDate, minConf, maxConf, page, pageSize, fetchPage]);

  useEffect(() => { load(); }, [load, refreshSignal]);

  const pages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { if (page > pages) setPage(1); }, [pages, page]);

  const uniqueLabels = useMemo(() => {
    const s = new Set();
    rows.forEach(r => r.label && s.add(r.label));
    return Array.from(s);
  }, [rows]);

  return (
    <Card className="mt-10" aria-label="History" data-testid="history-table">
      <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="flex-1 space-y-2">
          <label className="block text-xs font-medium">Search</label>
          <Input data-testid="search-input" placeholder="Search name or label..." value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Search history" />
        </div>
        <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
          <div>
            <label className="block text-[11px] font-medium">Label</label>
            <Select value={labelFilter} onChange={(e) => { setLabelFilter(e.target.value); setPage(1); }}>
              <option value="">All</option>
              {uniqueLabels.map(l => <option key={l} value={l}>{l}</option>)}
            </Select>
          </div>
          <div>
            <label className="block text-[11px] font-medium">From</label>
            <Input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(1); }} />
          </div>
          <div>
            <label className="block text-[11px] font-medium">To</label>
            <Input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(1); }} />
          </div>
          <div>
            <label className="block text-[11px] font-medium">Min Conf</label>
            <Input type="number" min="0" max="1" step="0.01" value={minConf} onChange={(e) => { setMinConf(e.target.value); setPage(1); }} />
          </div>
          <div>
            <label className="block text-[11px] font-medium">Max Conf</label>
            <Input type="number" min="0" max="1" step="0.01" value={maxConf} onChange={(e) => { setMaxConf(e.target.value); setPage(1); }} />
          </div>
          <div>
            <label className="block text-[11px] font-medium">Page Size</label>
            <Select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {[5,10,20,50].map(ps => <option key={ps} value={ps}>{ps}</option>)}
            </Select>
          </div>
        </div>
      </div>
      {error && (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-xs text-red-600">{error} <Button onClick={load} className="ml-2">Retry</Button></div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px] text-left text-sm">
          <thead>
            <tr className="text-xs text-black">
              <th className="py-2 pr-4 font-medium">Thumbnail</th>
              <th className="py-2 pr-4 font-medium">Name/ID</th>
              <th className="py-2 pr-4 font-medium">Label</th>
              <th className="py-2 pr-4 font-medium">Confidence</th>
              <th className="py-2 pr-4 font-medium">Created</th>
              <th className="py-2 pr-4 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 5 }).map((_,i) => (
              <tr key={i} className="border-t border-default/40">
                <td className="py-2 pr-4"><Skeleton className="h-14 w-14" /></td>
                <td className="py-2 pr-4"><Skeleton className="h-4 w-24" /></td>
                <td className="py-2 pr-4"><Skeleton className="h-4 w-16" /></td>
                <td className="py-2 pr-4"><Skeleton className="h-4 w-20" /></td>
                <td className="py-2 pr-4"><Skeleton className="h-4 w-28" /></td>
                <td className="py-2 pr-4"><Skeleton className="h-7 w-32" /></td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-sm text-black">
                  No records found. {debouncedQ || labelFilter || fromDate || toDate || minConf || maxConf ? 'Adjust filters or search.' : 'Upload and save a pallet to get started.'}
                </td>
              </tr>
            )}
            {!loading && rows.map(r => {
              const created = new Date(r.createdAt);
              const rel = timeAgo(created);
              return (
                <tr key={r.id} className="border-t border-default/40">
                  <td className="py-2 pr-4">
                    <button onClick={() => setViewImage(r)} className="group relative block h-14 w-14 overflow-hidden rounded-lg border focus:outline-none focus:ring-2">
                      <img src={r.imageUrl} alt={r.name} className="h-full w-full object-cover" />
                    </button>
                  </td>
                  <td className="py-2 pr-4 font-medium">{r.name}</td>
                  <td className="py-2 pr-4"><Badge>{r.label}</Badge></td>
                  <td className="py-2 pr-4 w-40">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-medium">{formatConfidence(r.confidence)}</span>
                      <Progress value={Math.round(r.confidence * 100)} />
                    </div>
                  </td>
                  <td className="py-2 pr-4 text-xs" title={created.toLocaleString()}>{rel}</td>
                  <td className="py-2 pr-4">
                    <div className="flex flex-wrap gap-1">
                      <Button onClick={() => setViewJSON(r)} className="text-[11px]">View</Button>
                      <Button data-testid="delete-button" onClick={() => { if (window.confirm('Delete this record?')) onDelete(r.id).catch(()=>{}); }} className="text-[11px]">Delete</Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
        <div className="text-xs text-secondary">Page {page} of {pages} ({total} total)</div>
        <div className="flex items-center gap-2">
          <Button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p-1))}>Prev</Button>
          <Button disabled={page >= pages} onClick={() => setPage(p => Math.min(pages, p+1))}>Next</Button>
        </div>
      </div>

      <Dialog open={!!viewImage} onClose={() => setViewImage(null)} title={viewImage?.name || 'Image'}>
        {viewImage && <img src={viewImage.imageUrl} alt={viewImage.name} className="max-h-[70vh] w-full object-contain" />}
      </Dialog>
      <Dialog open={!!viewJSON} onClose={() => setViewJSON(null)} title={viewJSON?.name || 'Analysis Results'}>
        {viewJSON && (() => { const s = deriveSpecFromRecord(viewJSON); return (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
              <div className="flex items-center justify-between gap-3 py-1"><span className="text-sm text-black">Material</span><span className="text-xs font-medium">{s.material ?? '—'}</span></div>
              <div className="flex items-center justify-between gap-3 py-1"><span className="text-sm text-black">Lasttyp</span><span className="text-xs font-medium">{s.lasttyp ?? '—'}</span></div>
              <div className="flex items-center justify-between gap-3 py-1"><span className="text-sm text-black">Stapelbarhet</span><span className="text-xs font-medium">{s.stapelbarhet ?? '—'}</span></div>
              <div className="flex items-center justify-between gap-3 py-1"><span className="text-sm text-black">Trycktålighet</span><span className="text-xs font-medium">{s.trycktalighet ?? '—'}</span></div>
              <div className="flex items-center justify-between gap-3 py-1"><span className="text-sm text-black">Viktkategori</span><span className="text-xs font-medium">{s.viktkategori ?? '—'}</span></div>
              <div className="flex items-center justify-between gap-3 py-1"><span className="text-sm text-black">Ömtålig</span><span className="text-xs font-medium">{s.omtalighet ?? '—'}</span></div>
              <div className="flex items-center justify-between gap-3 py-1"><span className="text-sm text-black">Förpackning</span><span className="text-xs font-medium">{s.forpackning ?? '—'}</span></div>
              <div className="flex items-center justify-between gap-3 py-1"><span className="text-sm text-black">Form</span><span className="text-xs font-medium">{s.form ?? '—'}</span></div>
            </div>
          </div>
        ); })()}
      </Dialog>
    </Card>
  );
}

function timeAgo(date) {
  const now = Date.now();
  const diff = (now - date.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return `${Math.floor(diff/86400)}d ago`;
}

export default HistoryTable;
